'use client';

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { m } from 'motion/react';
import { css } from 'styled-system/css';

import { Lift, Reveal } from '@shaojiang/components/motion';
import { Swatch } from '@shaojiang/components/site/Swatch';
import { Button } from '@shaojiang/components/ui/button';
import { Collapsible } from '@shaojiang/components/ui/collapsible';
import type { Tone } from '@shaojiang/theme/preset/recipes/site/tones';

export interface Chapter {
  years: string;
  company: string;
  title: string;
}

const TONES: Tone[] = ['cobalt', 'leaf', 'sun', 'tomato'];

const row = css({
  display: 'grid',
  gridTemplateColumns: { base: '1fr auto', md: '7rem 1fr auto' },
  gap: { base: '2', md: '6' },
  alignItems: 'center',
  px: '6',
  py: '5',
  borderRadius: 'media',
  bg: 'bg.surface',
  boxShadow: 'card',
});

function Row({ chapter, index }: { chapter: Chapter; index: number }) {
  return (
    <Reveal index={index} columns={1}>
      <Lift axis='x' amount={0.6} className={row}>
        <span
          className={css({
            textStyle: 'label',
            color: 'fg.muted',
            mdDown: { gridColumn: 'span 2' },
          })}
        >
          {chapter.years}
        </span>
        <span className={css({ textStyle: 'h3' })}>
          {chapter.company}{' '}
          <span className={css({ color: 'fg.muted', fontWeight: '600' })}>· {chapter.title}</span>
        </span>
        <Swatch tone={TONES[index % TONES.length]} />
      </Lift>
    </Reveal>
  );
}

/** The work history as chapters; the oldest ones fold away. */
export function Chapters({
  chapters,
  visible = 6,
  earlierLabel,
}: {
  chapters: Chapter[];
  visible?: number;
  earlierLabel: string;
}) {
  const shown = chapters.slice(0, visible);
  const earlier = chapters.slice(visible);
  const [open, setOpen] = React.useState(false);

  return (
    <div className={css({ display: 'flex', flexDirection: 'column', gap: '3' })}>
      {shown.map((chapter, i) => (
        <Row key={chapter.company + chapter.years} chapter={chapter} index={i} />
      ))}
      {earlier.length > 0 && (
        <Collapsible.Root open={open} onOpenChange={({ open }) => setOpen(open)}>
          <Collapsible.Content>
            <div className={css({ display: 'flex', flexDirection: 'column', gap: '3', pb: '3' })}>
              {earlier.map((chapter, i) => (
                <Row key={chapter.company + chapter.years} chapter={chapter} index={visible + i} />
              ))}
            </div>
          </Collapsible.Content>
          <Collapsible.Trigger asChild>
            <Button variant='outline' className={css({ alignSelf: 'flex-start', mt: '2' })}>
              {earlierLabel}
              <m.span
                animate={{ rotate: open ? 180 : 0 }}
                className={css({ display: 'inline-flex' })}
              >
                <ChevronDown />
              </m.span>
            </Button>
          </Collapsible.Trigger>
        </Collapsible.Root>
      )}
    </div>
  );
}
